import { NormalizedNumber, Percentage } from '@sparkdotfi/common-universal'

import { NativeAssetInfo } from '@/config/chain/types'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { MarketSparkRewards } from '@/features/spark-rewards/types'
import { MarketInfo, Reserve, UserPosition } from '@/features/sparklend/common/data/market-info/marketInfo'
import { ReserveStatus } from '@/features/sparklend/common/data/market-info/reserve-status'
import { MarketWalletInfo } from '@/features/sparklend/common/data/useMarketWalletInfo'
import { assetCanBeBorrowed } from '@/features/sparklend/common/utils/assetCanBeBorrowed'
import { SparkRewardsByReserve } from '@/features/sparklend/markets/logic/useSparkRewardsByReserve'
import { SparkLendToken } from '@/features/sparklend/tokens/SparkLendToken'
import { applyTransformers } from '@/utils/applyTransformers'

export interface Deposit {
  token: SparkLendToken
  reserveStatus: ReserveStatus
  balance: NormalizedNumber
  deposit: NormalizedNumber
  supplyAPY: Percentage | undefined
  isUsedAsCollateral: boolean
  sparkRewards?: MarketSparkRewards[]
}

export interface Borrow {
  token: SparkLendToken
  reserve: Reserve
  proxyMarket?: {
    token: SparkLendToken
  }
  available: NormalizedNumber
  debt: NormalizedNumber
  borrowAPY: Percentage | undefined
  sparkRewards?: MarketSparkRewards[]
}

export interface GetDepositsParams {
  marketInfo: MarketInfo
  walletInfo: MarketWalletInfo
  nativeAssetInfo: NativeAssetInfo
  sparkRewardsByReserve: SparkRewardsByReserve
}
export function getDeposits({
  marketInfo,
  walletInfo,
  nativeAssetInfo,
  sparkRewardsByReserve,
}: GetDepositsParams): Deposit[] {
  return marketInfo.userPositions
    .map((position) => {
      return applyTransformers({ position, marketInfo, walletInfo, nativeAssetInfo, sparkRewardsByReserve })([
        hideFrozenAssetIfNotDeposited,
        transformNativeAssetDeposit,
        transformDefaultDeposit,
      ])
    })
    .filter(Boolean)
}

interface DepositTransformerParams extends GetDepositsParams {
  position: UserPosition
  nativeAssetInfo: NativeAssetInfo
}

function transformNativeAssetDeposit({
  position,
  marketInfo,
  walletInfo,
  nativeAssetInfo,
  sparkRewardsByReserve,
}: DepositTransformerParams): Deposit | undefined {
  if (position.reserve.token.symbol !== nativeAssetInfo.wrappedNativeAssetSymbol) {
    return undefined
  }
  const deposit = transformDefaultDeposit({ position, marketInfo, walletInfo, nativeAssetInfo, sparkRewardsByReserve })

  return {
    ...deposit,
    balance: walletInfo
      .findWalletBalanceForToken(position.reserve.token)
      .plus(walletInfo.findWalletBalanceForSymbol(nativeAssetInfo.nativeAssetSymbol)),
  }
}

function transformDefaultDeposit({ position, walletInfo, sparkRewardsByReserve }: DepositTransformerParams): Deposit {
  const sparkRewards = sparkRewardsByReserve[position.reserve.token.address]?.supply

  return {
    token: position.reserve.token,
    reserveStatus: position.reserve.status,
    balance: walletInfo.findWalletBalanceForToken(position.reserve.token),
    deposit: position.collateralBalance,
    supplyAPY: position.reserve.supplyAPY,
    isUsedAsCollateral: position.reserve.usageAsCollateralEnabledOnUser,
    sparkRewards,
  }
}

function hideFrozenAssetIfNotDeposited({ position }: DepositTransformerParams): null | undefined {
  if (position.reserve.status === 'frozen' && position.collateralBalance.isZero()) {
    return null
  }
}

export interface GetBorrowsParams {
  marketInfo: MarketInfo
  nativeAssetInfo: NativeAssetInfo
  sparkRewardsByReserve: SparkRewardsByReserve
}

export function getBorrows({ marketInfo, nativeAssetInfo, sparkRewardsByReserve }: GetBorrowsParams): Borrow[] {
  return marketInfo.userPositions
    .filter((position) => assetCanBeBorrowed(position.reserve) || position.borrowBalance.gt(0))
    .map((position) => {
      return applyTransformers({ position, marketInfo, nativeAssetInfo, sparkRewardsByReserve })([
        transformNativeAssetBorrow,
        transformDefaultBorrow,
      ])
    })
    .filter(Boolean)
}

interface BorrowTransformerParams extends GetBorrowsParams {
  position: UserPosition
  nativeAssetInfo: NativeAssetInfo
  sparkRewardsByReserve: SparkRewardsByReserve
}

function transformNativeAssetBorrow({
  position,
  marketInfo,
  nativeAssetInfo,
  sparkRewardsByReserve,
}: BorrowTransformerParams): Borrow | undefined {
  if (position.reserve.token.symbol !== nativeAssetInfo.wrappedNativeAssetSymbol) {
    return undefined
  }
  const borrow = transformDefaultBorrow({ position, marketInfo, nativeAssetInfo, sparkRewardsByReserve })

  return {
    ...borrow,
    available: position.reserve.availableLiquidity,
    debt: position.borrowBalance,
    borrowAPY: position.reserve.variableBorrowApy,
  }
}

function transformDefaultBorrow({ position, marketInfo, sparkRewardsByReserve }: BorrowTransformerParams): Borrow {
  const sparkRewards = sparkRewardsByReserve[position.reserve.token.address]?.borrow
  const usdc = marketInfo.findBySymbol(TokenSymbol('USDC'))
  const isUsdcUsdsProxyMarket =
    import.meta.env.VITE_FEATURE_BORROW_USDC_FROM_USDS === '1' &&
    position.reserve.token.symbol === TokenSymbol('USDS') &&
    usdc

  return {
    token: position.reserve.token,
    reserve: position.reserve,
    proxyMarket: isUsdcUsdsProxyMarket ? { token: usdc } : undefined,
    available: position.reserve.availableLiquidity,
    debt: position.borrowBalance,
    borrowAPY: position.reserve.variableBorrowApy,
    sparkRewards,
  }
}
